import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import {
  BookingsTabControls,
  CalendarTabControls,
  Controls,
  FindASpaceTabControls,
  MeetingsTabControls,
  PreferenceDetails,
  PreferencesControlsConfig,
  ServiceOrdersTabControls,
  PreferencesBookingsData,
  PreferencesCalendarData,
  PreferencesFindASpaceData,
  PreferencesMeetingsData,
  PreferencesServiceOrdersData,
  PreferencesViewData,
  SelectOption,
} from './models/preferences.model';

import { ALL_SELECTED } from './models/preferences.model';
import { DataContextService } from '../datacontext.service';
import { COUNTRIES } from 'src/app/shared/utils/constants';

export interface PreferencesTabConfigDTO {
  id: number;
  name: string;
  tabFields: PreferencesTabFieldDTO[];
  subTabs: PreferencesSubTabFieldDTO[];
}

export interface PreferencesTabFieldDTO {
  id: number;
  name: string;
}

export interface PreferencesSubTabFieldDTO {
  name: string;
  tabFields: PreferencesTabFieldDTO[];
  includeDelete?: boolean;
}

@Injectable()
export class PreferencesConfigService {
  config: PreferencesControlsConfig;
  config$: ReplaySubject<PreferencesControlsConfig> = new ReplaySubject();

  constructor(private hotelingService: DataContextService) {}

  getConfig(): Observable<PreferencesControlsConfig> {
    if (this.config) return of(this.config);
    return this.hotelingService.getData<{ data: PreferencesTabConfigDTO[] }>('Views/tabs').pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          // TODO: Error toast
        }
      }),
      map((config: PreferencesTabConfigDTO[]) => {
        return this.setupConfig(config);
      }),
      tap((config: PreferencesControlsConfig) => {
        this.config = config;
        this.config$.next(config);
      })
    );
  }

  private setupConfig(config: PreferencesTabConfigDTO[]): PreferencesControlsConfig {
    const result = {};

    config.forEach((tab: PreferencesTabConfigDTO) => {
      result[TAB_NAME_ADAPTER[tab.name]] = {};

      result[TAB_NAME_ADAPTER[tab.name]] = tab.tabFields.reduce(
        (prev, curr: PreferencesTabFieldDTO) => ({ ...prev, ...{ [FIELD_PROPERTY_ADAPTER[tab.name][curr.name]]: curr.id } }),
        {}
      );

      tab.subTabs.forEach((subTab: PreferencesSubTabFieldDTO) => {
        result[TAB_NAME_ADAPTER[tab.name]] = {
          ...result[TAB_NAME_ADAPTER[tab.name]],
          ...subTab.tabFields.reduce(
            (prev, curr: PreferencesTabFieldDTO) => ({
              ...prev,
              ...{ [FIELD_PROPERTY_ADAPTER[tab.name][subTab.name][curr.name]]: curr.id },
            }),
            {}
          ),
        };
      });
    });

    return result;
  }

  getPathById(id: number): { tab: string; control: string } | void {
    // tslint:disable-next-line:forin
    for (const tab in this.config) {
      for (const control in this.config[tab]) {
        if (this.config[tab][control] === id) {
          return { tab, control };
        }
      }
    }
  }

  parseViewDetails(data: PreferenceDetails): PreferencesViewData {
    const tabs: PreferencesControlsConfig = {
      findASpaceTab: {},
      calendarTab: {},
      meetingsTab: {},
      bookingsTab: {},
      serviceOrdersTab: {},
    };
    data.viewDetails.map((field) => {
      const path = this.getPathById(field.tabFieldId);
      if (path) {
        if (
          path.control === FindASpaceTabControls.Capacity ||
          path.control === CalendarTabControls.Capacity ||
          (path.tab === Controls.FindASpaceTab && path.control === FindASpaceTabControls.Type) ||
          (path.tab === Controls.FindASpaceTab && path.control === FindASpaceTabControls.Category)
        ) {
          tabs[path.tab][path.control] = field.value;
        } else if (
          path.control === ServiceOrdersTabControls.InOfficeIncludeDelete ||
          path.control === ServiceOrdersTabControls.VirtualIncludeDelete
        ) {
          tabs[path.tab][path.control] = Boolean(Number(field.value));
        } else {
          tabs[path.tab][path.control] = this.convertValueToOptions(field.value);
        }
      }
    });

    const findASpaceTab = new PreferencesFindASpaceData(
      tabs.findASpaceTab.spaceFeatures,
      tabs.findASpaceTab.capacity,
      tabs.findASpaceTab.type
    );

    const calendarTab = new PreferencesCalendarData(tabs.calendarTab.roomType,tabs.calendarTab.spaceFeatures, tabs.calendarTab.capacity);

    const meetingTab = new PreferencesMeetingsData(
      tabs.meetingsTab.dashboardRequestType,
      tabs.meetingsTab.dashboardMeetingType,
      tabs.meetingsTab.dashboardStatus,
      tabs.meetingsTab.meetingRequestType,
      tabs.meetingsTab.meetingStatus,
      tabs.meetingsTab.virtualMeetingStatus,
      tabs.meetingsTab.unfulfilledSpaceType
    );

    const allBookingsTab = new PreferencesBookingsData(
      tabs.bookingsTab.serviceOrders,
      tabs.bookingsTab.spaceType,
      tabs.bookingsTab.meetingType,
      tabs.bookingsTab.dashboardStatus,
      tabs.bookingsTab.waitListStatus
    );

    const serviceOrderTab = new PreferencesServiceOrdersData(
      tabs.serviceOrdersTab.type,
      tabs.serviceOrdersTab.serviceOrder,
      tabs.serviceOrdersTab.status,
      tabs.serviceOrdersTab.virtualServiceOrders,
      tabs.serviceOrdersTab.virtualStatus,
      tabs.serviceOrdersTab.inOfficeIncludeDelete,
      tabs.serviceOrdersTab.virtualIncludeDelete
    );

    return new PreferencesViewData(
      data.id,
      data.name,
      data.isFavourite,
      data.isActive,
      // this.convertValueToOptions(data.countries,true),
      this.convertValueToOptions(data.buildings),
      this.convertValueToOptions(data.floors),
      data.assignedToMe,
      data.recordsPerPage,
      data.revision,
      findASpaceTab,
      calendarTab,
      meetingTab,
      allBookingsTab,
      serviceOrderTab,
      data.allViews
    );
  }

  private convertValueToOptions(value: string,bindName?:Boolean): SelectOption[] {
    if (!value) {
      return [];
    }

    if (value === 'All') {
      return ALL_SELECTED;
    }

    return value.split(',').map((id) => ({
      value: isNaN(Number(id)) ? id : Number(id),
      name: bindName ? COUNTRIES.filter(i => i.id == Number(id))[0].name : '',
    }));
  }
}

const TAB_NAME_ADAPTER = {
  FAS: Controls.FindASpaceTab,
  Calendar: Controls.CalendarTab,
  Meetings: Controls.MeetingsTab,
  Bookings: Controls.BookingsTab,
  'Service orders': Controls.ServiceOrdersTab,
};

const FIELD_PROPERTY_ADAPTER = {
  FAS: {
    'Space features': FindASpaceTabControls.SpaceFeatures,
    'Space type': FindASpaceTabControls.Type,
    Capacity: FindASpaceTabControls.Capacity,
  },
  Calendar: {
    'Space type': CalendarTabControls.RoomType,
    Capacity: CalendarTabControls.Capacity,
    'Space features': CalendarTabControls.SpaceFeatures,
  },
  Meetings: {
    'Meetings Dashboard': {
      'Meeting request type': MeetingsTabControls.DashboardRequestType,
      Status: MeetingsTabControls.DashboardStatus,
      'Meeting type': MeetingsTabControls.DashboardMeetingType,
    },
    'In-office meeting Requests': {
      'Request type': MeetingsTabControls.MeetingRequestType,
      Status: MeetingsTabControls.MeetingStatus,
    },
    'Virtual meeting Requests': {
      Status: MeetingsTabControls.VirtualMeetingStatus,
    },
    'Unfulfilled team requests': {
      'Space type': MeetingsTabControls.UnfulfilledSpaceType,
    },
  },
  Bookings: {
    'All bookings': {
      'Service orders': BookingsTabControls.ServiceOrders,
      'Space type': BookingsTabControls.SpaceType,
      'Meeting type': BookingsTabControls.MeetingType,
      Status: BookingsTabControls.DashboardStatus,
    },
    Waitlist: {
      Status: BookingsTabControls.WaitListStatus,
    },
  },
  'Service orders': {
    'In-office': {
      Type: ServiceOrdersTabControls.Type,
      'Service orders': ServiceOrdersTabControls.ServiceOrder,
      Status: ServiceOrdersTabControls.Status,
      'Include Deleted': ServiceOrdersTabControls.InOfficeIncludeDelete,
    },
    Virtual: {
      'Service orders': ServiceOrdersTabControls.VirtualServiceOrders,
      Status: ServiceOrdersTabControls.VirtualStatus,
      'Include Deleted': ServiceOrdersTabControls.VirtualIncludeDelete,
    },
  },
};
